import {
  buildCollection,
  buildEntityCallbacks,
  type EntityCollection,
  type EntityOnSaveProps,
  type EntityStatus,
} from "@firecms/core";
import { buildAccountEntityActions } from "../actions/buildAccountEntityActions";
import { getMyPermissions } from "../firebase/getMyPermissions";
import { mergeAuthExtras, type AuthExtras } from "../types/auth";

export type Account = {
  name: string;
  status: string;
  createdByUserId?: string;
};

const accountsCallbacks = buildEntityCallbacks({
  onPreSave({
    values,
    status,
    context,
  }: EntityOnSaveProps & { status?: EntityStatus }) {
    if (status === "new" && context?.authController?.user?.uid) {
      values.createdByUserId = context.authController.user.uid;
    }
    return Promise.resolve(values);
  },
  async onSaveSuccess({ status, context }) {
    if (status !== "new") {
      return;
    }
    const userId = context.authController.user?.uid;
    if (!userId) {
      return;
    }
    try {
      context.authController.setExtra(
        mergeAuthExtras(
          context.authController.extra as AuthExtras | undefined,
          await getMyPermissions()
        )
      );
    } catch (error) {
      console.warn("getMyPermissions failed after account create", error);
    }
  },
});

const collection: EntityCollection<Account> = {
  name: "Accounts",
  singularName: "Account",
  defaultSize: "xs",
  description:
    "A client or organization (for example a company). Everything you configure—content and live sessions—belongs to an account.",
  id: "accounts",
  path: "accounts",
  icon: "Business",
  group: "Admin",
  permissions: ({ authController }) => {
    const extra = authController.extra as AuthExtras | undefined;
    const superManager = extra?.isSuperManager === true;
    return {
      read: superManager,
      create: superManager,
      edit: false,
      delete: false,
    };
  },
  properties: {
    name: {
      name: "Name",
      validation: { required: true },
      dataType: "string",
    },
    status: {
      name: "Status",
      validation: { required: true },
      dataType: "string",
      enumValues: {
        enabled: "Enabled",
        disabled: "Disabled",
      },
    },
    createdByUserId: {
      name: "Created by",
      dataType: "string",
      readOnly: true,
      disabled: true,
      hideFromCollection: true,
    },
  },
  callbacks: accountsCallbacks,
  entityActions: buildAccountEntityActions(),
};

export const accountsCollection = buildCollection<Account>(collection);
